// components/HomeScreen.jsx
import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import {
  Calculator,
  Plus,
  Home,
  TrendingUp,
  FileText,
  Trash2,
  Copy,
  Share2,
  DollarSign,
  Users,
  LayoutList,
  Mail,
} from 'lucide-react';
import PocketCampaignsTab from './home/PocketCampaignsTab';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getShare } from '../services/shareService';
import { interestUnlocksSharedAddress } from '../utils/shareRecipientAddressVisibility';
import { getActiveBrokerDashboardShareUids } from '../services/dataService';
import CmbRates from './CmbRates';
import PrivateLenderLoansPage from './privateLenderOffer/PrivateLenderLoansPage';
import CapitalInvestorsPage from './privateLenderOffer/CapitalInvestorsPage';
import InvestorFundsView from './privateLenderOffer/InvestorFundsView';
import { isOwnedDeal } from '../utils/dealPurpose';
import { formatPricePerDoor } from '../utils/formatPricePerDoor';

function PropertyCard({
  property,
  index,
  isOwner,
  isNewClientProperty,
  onSelect,
  onShare,
  onDuplicate,
  onDelete,
  t,
  isBrokerView = false,
  purchaseOffersModuleEnabled = false,
  shareRecipientCount = 0,
}) {
  const fullAddress = [
    property.address,
    property.city,
    property.province,
    property.postalCode,
  ]
    .filter(Boolean)
    .join(', ');
  const brokerCount = getActiveBrokerDashboardShareUids(property).length;
  const sharedWithBroker = brokerCount > 0;
  /** Nombre de personnes ayant le lien / tableau de bord : réservé au propriétaire du deal uniquement */
  const displayShareCount = isOwner(property) ? shareRecipientCount : 0;
  const showSharedLine = displayShareCount > 0 || sharedWithBroker;
  const personLabel = displayShareCount === 1
    ? t('home.sharedWithCount').replace('{count}', displayShareCount)
    : (t('home.sharedWithCount_plural') || t('home.sharedWithCount')).replace('{count}', displayShareCount);
  const shareLabel = sharedWithBroker && displayShareCount > 0
    ? t('home.sharedWithCountAndBroker').replace('{count}', String(displayShareCount))
    : sharedWithBroker
      ? (brokerCount > 1 ? t('home.sharedWithBrokers').replace('{count}', String(brokerCount)) : t('home.sharedWithBroker'))
      : personLabel;
  const shareColor = '#003e56';

  return (
    <div
      key={property.id || index}
      className="relative rounded-lg p-4 bg-gray-50 hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
      onClick={() => onSelect(property)}
    >
      {isNewClientProperty(property) && (
        <span className="mb-2 inline-block px-2 py-0.5 text-xs font-semibold bg-amber-400 text-amber-900 rounded">
          {t('home.newBadge')}
        </span>
      )}
      <div className="flex items-start gap-2 mb-3">
        <h3 className="min-w-0 flex-1 font-semibold text-lg break-words pr-1">
          {fullAddress || t('home.address.unset')}
          {isOwnedDeal(property) ? (
            <span className="ml-2 inline-block align-middle text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">
              {t('dealPurpose.badge.owned')}
            </span>
          ) : null}
          {isBrokerView && purchaseOffersModuleEnabled && isOwner(property) && property.brokerSaleMandate ? (
            <span className="ml-2 inline-block align-middle text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-900">
              {t(`listingMandateStatus.${property.listingMandateStatus || 'active'}`)}
            </span>
          ) : null}
        </h3>
        {isOwner(property) && (
          <div className="flex shrink-0 items-center gap-0.5 -mr-1 -mt-0.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShare?.(property.id);
              }}
              className={`p-1 rounded flex items-center justify-center ${
                brokerCount > 0
                  ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/80'
                  : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50/80'
              }`}
              aria-label={t('home.share')}
              title={brokerCount > 0 ? t('shareWithBroker.alreadyShared') : undefined}
              type="button"
            >
              <Share2 className="w-4 h-4 shrink-0" aria-hidden />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate?.(property);
              }}
              className="p-1 rounded text-gray-600 hover:text-gray-800 hover:bg-gray-100/80 flex items-center justify-center"
              aria-label={t('home.duplicate')}
              type="button"
            >
              <Copy className="w-4 h-4 shrink-0" aria-hidden />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(property);
              }}
              className="p-1 rounded text-red-600 hover:text-red-800 hover:bg-red-50/80 flex items-center justify-center"
              aria-label={t('home.delete')}
              type="button"
            >
              <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
            </button>
          </div>
        )}
        {!isOwner(property) && onShare && !(isBrokerView && property.fromClient) && (
          <button
            onClick={(e) => { e.stopPropagation(); onShare(property.id); }}
            className="shrink-0 p-1 text-blue-600 hover:text-blue-800"
            aria-label={t('home.share')}
            type="button"
          >
            <Share2 size={16} />
          </button>
        )}
      </div>
      <div className="flex flex-wrap text-sm text-gray-600 gap-y-1 mb-2">
        <div className="w-1/2 flex items-center">
          <Home className="w-4 h-4 mr-1 text-blue-600" />
          {property.numberOfUnits} {t('home.units')}
        </div>
        <div className="w-1/2 flex items-center">
          <DollarSign className="w-4 h-4 mr-1 text-green-600" />
          {Number(property.purchasePrice || 0).toLocaleString('fr-CA')}$
        </div>
        <div className="w-1/2 flex items-center">
          <Calculator className="w-4 h-4 mr-1 text-purple-600" />
          {formatPricePerDoor(property.purchasePrice, property.numberOfUnits)}{t('home.pricePerDoor')}
        </div>
        {showSharedLine && (
          <div className="w-1/2 flex items-center">
            <Users className="w-4 h-4 mr-1 shrink-0" style={{ color: shareColor }} />
            <span>{shareLabel}</span>
          </div>
        )}
      </div>
      <div
        className={`mt-2 inline-block px-2 py-1 text-sm font-medium rounded ${
          (property.effectiveNetIncome ?? 0) >= 0
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`}
      >
        {(property.effectiveNetIncome ?? 0) >= 0 ? '+' : ''}
        {Math.round(property.effectiveNetIncome ?? 0).toLocaleString('fr-CA')}{t('home.perYear')}
      </div>
    </div>
  );
}

function SharedWithMeCard({ item, onSelect, onRemove, t }) {
  const { currentUser } = useAuth();
  const [unlockedAddressParts, setUnlockedAddressParts] = useState(null);

  const previewLooksMasked =
    item.anonymizeAddress === true ||
    (item.anonymizeAddress !== false && /^\*{4}/.test(String(item.address || '').trim()));

  useEffect(() => {
    if (!currentUser?.uid || !item.shareToken || !previewLooksMasked) {
      setUnlockedAddressParts(null);
      return undefined;
    }
    const q = query(
      collection(firestore, 'shares', item.shareToken, 'interests'),
      where('recipientUid', '==', currentUser.uid),
    );
    let cancelled = false;
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) {
          setUnlockedAddressParts(null);
          return;
        }
        const st = snap.docs[0].data()?.status;
        if (!interestUnlocksSharedAddress(st)) {
          setUnlockedAddressParts(null);
          return;
        }
        getShare(item.shareToken)
          .then((share) => {
            if (cancelled) return;
            const p = share?.snapshot?.property;
            if (p) {
              setUnlockedAddressParts({
                address: p.address || '',
                postalCode: p.postalCode || '',
              });
            } else {
              setUnlockedAddressParts(null);
            }
          })
          .catch(() => {
            if (!cancelled) setUnlockedAddressParts(null);
          });
      },
      () => setUnlockedAddressParts(null),
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [currentUser?.uid, item.shareToken, previewLooksMasked]);

  const address = unlockedAddressParts?.address ?? item.address;
  const postalCode = unlockedAddressParts?.postalCode ?? item.postalCode;
  const fullAddress = [address, item.city, item.province, postalCode]
    .filter(Boolean)
    .join(', ') || t('home.address.unset');
  return (
    <div
      role="button"
      tabIndex={0}
      className="relative border rounded-lg p-4 bg-gray-50 hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onSelect(item.id, item.shareToken)}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(item.id, item.shareToken)}
    >
      {(item.cessionRevoked || (!item.seen && !item.cessionRevoked)) && (
        <span
          className={`mb-2 inline-block px-2 py-0.5 text-xs rounded ${
            item.cessionRevoked
              ? 'font-medium bg-slate-200 text-slate-700'
              : 'font-semibold bg-amber-400 text-amber-900'
          }`}
        >
          {item.cessionRevoked ? t('home.cessionRevoked') : t('home.newBadge')}
        </span>
      )}
      <div className="flex items-start gap-2 mb-3">
        <h3 className="min-w-0 flex-1 font-semibold text-lg break-words pr-1">{fullAddress}</h3>
        {onRemove && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(item.id, item.shareToken); }}
            className="shrink-0 -mr-1 -mt-0.5 text-red-600 hover:text-red-800 p-1"
            aria-label={t('home.removeFromDashboard')}
            title={t('home.removeFromDashboard')}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
      <div className="flex flex-wrap text-sm text-gray-600 gap-y-1 mb-2">
        {item.numberOfUnits != null && (
          <div className="w-1/2 flex items-center">
            <Home className="w-4 h-4 mr-1 text-blue-600" />
            {item.numberOfUnits} {t('home.units')}
          </div>
        )}
        {item.purchasePrice != null && (
          <div className="w-1/2 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-green-600" />
            {Number(item.purchasePrice).toLocaleString('fr-CA')}$
          </div>
        )}
        {item.numberOfUnits != null && item.purchasePrice != null && (
          <div className="w-1/2 flex items-center">
            <Calculator className="w-4 h-4 mr-1 text-purple-600" />
            {formatPricePerDoor(item.purchasePrice, item.numberOfUnits)}
            {t('home.pricePerDoor')}
          </div>
        )}
      </div>
      {item.effectiveNetIncome != null && (
        <div
          className={`mt-2 inline-block px-2 py-1 text-sm font-medium rounded ${
            (item.effectiveNetIncome ?? 0) >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {(item.effectiveNetIncome ?? 0) >= 0 ? '+' : ''}
          {Math.round(item.effectiveNetIncome ?? 0).toLocaleString('fr-CA')}{t('home.perYear')}
        </div>
      )}
    </div>
  );
}

const HomeScreen = ({
  properties,
  sharedWithMe = [],
  onSelectSharedWithMe,
  onRemoveSharedWithMe,
  onNew,
  onSelect,
  onDelete,
  onDuplicate,
  onShare,
  onAbout,
  currentUserId,
  clients = [],
  isBrokerView = false,
  isPreteurPrive = false,
  financedDealsAsOwner = [],
  financedDealsAsLender = [],
  fundInvestorsAsInvestor = [],
  shareStatsVersion = 0,
  shareStats = {},
  pendingLoanDetailDeal = null,
  onConsumePendingLoanDetail = null,
  initialActiveTab = null,
  openFundInvestorId = null,
  showPocketCampaignsTab = false,
  purchaseOffersModuleEnabled = false,
  onResubmitPocketCampaign,
  /** Appelé quand l’utilisateur ouvre l’onglet « Immeubles partagés » (ex. fermer le bandeau vert). */
  onDismissShareAddedBanner,
}) => {
  const { t } = useLanguage();
  const isOwner = (p) => p.uid === currentUserId;
  const isNewClientProperty = (p) => p.fromClient && !p.brokerSeenAt;

  const financedPropertyIdsOwner = React.useMemo(
    () => new Set((financedDealsAsOwner || []).filter((d) => d.status !== 'cancelled').map((d) => d.propertyId).filter(Boolean)),
    [financedDealsAsOwner]
  );
  const financedPropertyIdsLender = React.useMemo(
    () => new Set((financedDealsAsLender || []).filter((d) => d.status !== 'cancelled').map((d) => d.propertyId).filter(Boolean)),
    [financedDealsAsLender]
  );

  const myAnalyses = React.useMemo(() => {
    if (isBrokerView) return properties.filter((p) => !p.fromClient);
    return properties.filter((p) => p.uid === currentUserId && !financedPropertyIdsOwner.has(p.id));
  }, [isBrokerView, properties, currentUserId, financedPropertyIdsOwner]);
  const financedProjects = React.useMemo(
    () => (isBrokerView ? [] : properties.filter((p) => p.uid === currentUserId && financedPropertyIdsOwner.has(p.id))),
    [isBrokerView, properties, currentUserId, financedPropertyIdsOwner]
  );
  const clientIdsSet = React.useMemo(() => new Set(clients.map((c) => c.id)), [clients]);
  const clientPropertiesRaw = isBrokerView ? properties.filter((p) => p.fromClient) : [];
  const clientProperties = isPreteurPrive
    ? clientPropertiesRaw.filter((p) => !financedPropertyIdsLender.has(p.id))
    : clientPropertiesRaw;
  const financedProperties = isPreteurPrive
    ? clientPropertiesRaw.filter((p) => financedPropertyIdsLender.has(p.id))
    : [];
  const getClientLabel = (clientId) => {
    if (clientId === '_sans_client_') return null;
    const c = clients.find((x) => x.id === clientId);
    return c ? (c.name || c.email || c.id) : (t('home.clientDeleted') || 'Client supprimé');
  };

  const byClientEntries = React.useMemo(() => {
    const map = {};
    clientProperties.forEach((p) => {
      const cid = p.clientId || '_sans_client_';
      if (!map[cid]) map[cid] = [];
      map[cid].push(p);
    });
    const entries = Object.entries(map);
    entries.sort(([idA], [idB]) => {
      if (idA === '_sans_client_') return 1;
      if (idB === '_sans_client_') return -1;
      const cA = clients.find((x) => x.id === idA);
      const cB = clients.find((x) => x.id === idB);
      const labelA = cA ? (cA.name || cA.email || idA) : idA;
      const labelB = cB ? (cB.name || cB.email || idB) : idB;
      return String(labelA).localeCompare(String(labelB));
    });
    return entries;
  }, [clientProperties, clients]);

  const byFinancedClientEntries = React.useMemo(() => {
    const map = {};
    financedProperties.forEach((p) => {
      const cid = p.clientId || '_sans_client_';
      if (!map[cid]) map[cid] = [];
      map[cid].push(p);
    });
    const entries = Object.entries(map);
    entries.sort(([idA], [idB]) => {
      if (idA === '_sans_client_') return 1;
      if (idB === '_sans_client_') return -1;
      const cA = clients.find((x) => x.id === idA);
      const cB = clients.find((x) => x.id === idB);
      const labelA = cA ? (cA.name || cA.email || idA) : idA;
      const labelB = cB ? (cB.name || cB.email || idB) : idB;
      return String(labelA).localeCompare(String(labelB));
    });
    return entries;
  }, [financedProperties, clients]);

  const totalCount = properties.length;
  const showBrokerSections = isBrokerView && (myAnalyses.length > 0 || clientProperties.length > 0 || financedProperties.length > 0 || sharedWithMe.length > 0);
  // Prêteur privé est aussi un « broker » (BROKER_PERSONAS) : sans analyses ni clients, showBrokerSections
  // devient false et !isBrokerView bloquait showClientTabs — il faut toujours offrir la barre d’onglets prêteur.
  const showClientTabs =
    isPreteurPrive ||
    (!isBrokerView && (properties.length > 0 || sharedWithMe.length > 0));
  const showFinancedProjectsTab = !isBrokerView && financedProjects.length > 0;
  const showFinancedPropertiesTab = isPreteurPrive && financedProperties.length > 0;
  const showLoansManagementTab = isPreteurPrive;
  const showCapitalInvestorsTab = isPreteurPrive;
  const hasFundInvestorPlacements = (fundInvestorsAsInvestor?.length ?? 0) > 0;
  const isCapitalInvestorOnly =
    hasFundInvestorPlacements &&
    properties.length === 0 &&
    sharedWithMe.length === 0 &&
    !isBrokerView &&
    (financedDealsAsLender?.length ?? 0) === 0 &&
    (financedDealsAsOwner?.length ?? 0) === 0;
  const [activeTab, setActiveTab] = React.useState(isCapitalInvestorOnly ? 'investorFunds' : 'myAnalyses');
  const [mandatesOnlyFilter, setMandatesOnlyFilter] = React.useState(false);
  const myAnalysesDisplayed = React.useMemo(() => {
    if (!mandatesOnlyFilter || !purchaseOffersModuleEnabled || !isBrokerView) return myAnalyses;
    return myAnalyses.filter((p) => p.brokerSaleMandate);
  }, [myAnalyses, mandatesOnlyFilter, purchaseOffersModuleEnabled, isBrokerView]);
  const privateLenderEmptyHub =
    isPreteurPrive && totalCount === 0 && sharedWithMe.length === 0;
  React.useEffect(() => {
    if (privateLenderEmptyHub) setActiveTab('loansManagement');
  }, [privateLenderEmptyHub]);
  React.useEffect(() => {
    if (pendingLoanDetailDeal?.id) setActiveTab('loansManagement');
  }, [pendingLoanDetailDeal?.id]);
  React.useEffect(() => {
    if (!initialActiveTab) return;
    setActiveTab(initialActiveTab);
  }, [initialActiveTab]);

  if (isCapitalInvestorOnly) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 p-3 sm:p-4 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8">
            <img src="/rentalyzer-logo.png" alt="Rentalyzer logo" width={200} className="inline-block" />
            <h1 className="text-4xl font-bold text-gray-800 mb-4 flex items-center justify-center">{t('home.title')}</h1>
            <p className="text-xl text-gray-600 mb-8">{t('home.subtitle')}</p>
          </div>
          <div className="mb-8">
            <p className="text-center text-gray-600 mb-4">{t('investorFunds.capitalInvestorOnlyMessage')}</p>
            <InvestorFundsView fundInvestors={fundInvestorsAsInvestor} openFundInvestorId={openFundInvestorId} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 p-3 sm:p-4 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-8" data-onboarding="home-hero">
          <img
            src="/rentalyzer-logo.png"
            alt="Rentalyzer logo"
            width={200}
            className="inline-block"
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-4 flex items-center justify-center">
            {t('home.title')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('home.subtitle')}
          </p>
        </div>

        {totalCount === 0 && sharedWithMe.length === 0 && !isPreteurPrive ? (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-6 text-center">{t('home.empty.title')}</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">{t('home.step1.title')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('home.step1.text')}
                </p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold mb-2">{t('home.step2.title')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('home.step2.text')}
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-2">{t('home.step3.title')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('home.step3.text')}
                </p>
              </div>
            </div>
            <div className="text-center">
              <button
                type="button"
                data-onboarding="home-empty-cta"
                onClick={onNew}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="inline-block mr-2" />
                {t('home.new.button')}
              </button>
            </div>
          </div>
        ) : showBrokerSections ? (
          <div className="mb-8">
            {/* Onglets */}
            <div className="bg-white rounded-t-lg shadow border-b border-gray-200 overflow-hidden">
              <div className="flex overflow-x-auto scrollbar-hide" data-onboarding="home-tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab('myAnalyses')}
                  className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                    activeTab === 'myAnalyses'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FileText className="w-4 h-4 shrink-0" />
                  {t('home.sectionMyAnalyses')}
                  {myAnalyses.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'myAnalyses' ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      {myAnalyses.length}
                    </span>
                  )}
                </button>
                {showPocketCampaignsTab && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('pocketCampaigns')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'pocketCampaigns'
                        ? 'bg-violet-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Mail className="w-4 h-4 shrink-0" />
                    {t('home.sectionPocketCampaigns')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setActiveTab('clientProperties')}
                  className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                    activeTab === 'clientProperties'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Users className="w-4 h-4 shrink-0" />
                  {t('home.sectionClientProperties')}
                  {clientProperties.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'clientProperties' ? 'bg-green-500' : 'bg-gray-300'}`}>
                      {clientProperties.length}
                    </span>
                  )}
                </button>
                {showFinancedPropertiesTab && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('financedProperties')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'financedProperties'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <DollarSign className="w-4 h-4 shrink-0" />
                    {t('home.sectionFinancedProperties')}
                    {financedProperties.length > 0 && (
                      <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'financedProperties' ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                        {financedProperties.length}
                      </span>
                    )}
                  </button>
                )}
                {showLoansManagementTab && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('loansManagement')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'loansManagement'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <LayoutList className="w-4 h-4 shrink-0" />
                    {t('home.sectionLoansManagement')}
                  </button>
                )}
                {showCapitalInvestorsTab && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('capitalInvestors')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'capitalInvestors'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Users className="w-4 h-4 shrink-0" />
                    {t('home.sectionCapitalInvestors')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onDismissShareAddedBanner?.();
                    setActiveTab('sharedWithMe');
                  }}
                  className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                    activeTab === 'sharedWithMe'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Share2 className="w-4 h-4 shrink-0" />
                  {t('home.tabSharedWithMe')}
                  {sharedWithMe.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'sharedWithMe' ? 'bg-indigo-500' : 'bg-gray-300'}`}>
                      {sharedWithMe.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Contenu onglet Mes Deals */}
            {activeTab === 'myAnalyses' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <div className="flex flex-col mb-4 md:flex-row md:justify-between md:items-center">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    {t('home.sectionMyAnalyses')}
                  </h2>
                  <button
                    type="button"
                    data-onboarding="home-new-deal"
                    onClick={onNew}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors mt-2 md:mt-0"
                  >
                    <Plus className="inline-block mr-1" size={18} />
                    {t('home.existing.new')}
                  </button>
                </div>
                {myAnalyses.length > 0 ? (
                  <>
                    {isBrokerView && purchaseOffersModuleEnabled ? (
                      <label className="flex items-start gap-2 text-sm text-gray-700 mb-3 cursor-pointer max-w-xl">
                        <input
                          type="checkbox"
                          className="mt-0.5 shrink-0"
                          checked={mandatesOnlyFilter}
                          onChange={(e) => setMandatesOnlyFilter(e.target.checked)}
                        />
                        <span>
                          <span className="font-medium block">{t('home.mandatesFilter')}</span>
                          <span className="text-xs text-gray-500 block mt-0.5">{t('home.mandatesFilterHint')}</span>
                        </span>
                      </label>
                    ) : null}
                    {myAnalysesDisplayed.length > 0 ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        {myAnalysesDisplayed.map((property, index) => (
                          <PropertyCard
                            key={property.id || index}
                            property={property}
                            index={index}
                            isOwner={isOwner}
                            isNewClientProperty={isNewClientProperty}
                            onSelect={onSelect}
                            onShare={onShare}
                            onDuplicate={onDuplicate}
                            onDelete={onDelete}
                            t={t}
                            isBrokerView={isBrokerView}
                            purchaseOffersModuleEnabled={purchaseOffersModuleEnabled}
                            shareRecipientCount={shareStats[property.id] ?? 0}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm py-2">{t('home.mandatesFilterEmpty')}</p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-sm py-2">{t('home.noMyAnalyses')}</p>
                )}
              </div>
            )}

            {showPocketCampaignsTab && activeTab === 'pocketCampaigns' && (
              <PocketCampaignsTab
                properties={properties}
                onResubmit={onResubmitPocketCampaign}
                t={t}
              />
            )}

            {/* Contenu onglet Immeubles Clients — regroupés par client */}
            {activeTab === 'clientProperties' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  {t('home.sectionClientProperties')}
                </h2>
                {clientProperties.length === 0 ? (
                  <p className="text-gray-500 text-sm py-2">{t('home.noClientProperties')}</p>
                ) : (
                  <div className="space-y-8">
                    {byClientEntries.map(([clientId, props]) => {
                      const label = getClientLabel(clientId);
                      const count = props.length;
                      const countLabel = count === 1
                        ? t('home.clientPropertiesCount_one')
                        : t('home.clientPropertiesCount_other').replace('{{count}}', String(count));
                      return (
                        <div key={clientId} className="border-l-4 border-green-400 pl-4">
                          <h3 className="font-medium text-gray-800 mb-1">
                            {label || t('home.unknownClient')}
                          </h3>
                          <p className="text-sm text-gray-500 mb-3">{countLabel}</p>
                          <div className="grid gap-4 md:grid-cols-2">
                            {props.map((property, index) => (
                              <PropertyCard
                                key={property.id || index}
                                property={property}
                                index={index}
                                isOwner={isOwner}
                                isNewClientProperty={isNewClientProperty}
                                onSelect={onSelect}
                                onShare={onShare}
                                onDuplicate={onDuplicate}
                                onDelete={onDelete}
                                t={t}
                                isBrokerView={isBrokerView}
                                purchaseOffersModuleEnabled={purchaseOffersModuleEnabled}
                                shareRecipientCount={shareStats[property.id] ?? 0}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Contenu onglet Immeubles financés (prêteur) — regroupés par client */}
            {activeTab === 'financedProperties' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  {t('home.sectionFinancedProperties')}
                </h2>
                {financedProperties.length === 0 ? (
                  <p className="text-gray-500 text-sm py-2">{t('home.noFinancedProperties')}</p>
                ) : (
                  <div className="space-y-8">
                    {byFinancedClientEntries.map(([clientId, props]) => {
                      const label = getClientLabel(clientId);
                      const count = props.length;
                      const countLabel = count === 1
                        ? t('home.clientPropertiesCount_one')
                        : t('home.clientPropertiesCount_other').replace('{{count}}', String(count));
                      return (
                        <div key={clientId} className="border-l-4 border-emerald-400 pl-4">
                          <h3 className="font-medium text-gray-800 mb-1">
                            {label || t('home.unknownClient')}
                          </h3>
                          <p className="text-sm text-gray-500 mb-3">{countLabel}</p>
                          <div className="grid gap-4 md:grid-cols-2">
                            {props.map((property, index) => (
                              <PropertyCard
                                key={property.id || index}
                                property={property}
                                index={index}
                                isOwner={isOwner}
                                isNewClientProperty={isNewClientProperty}
                                onSelect={onSelect}
                                onShare={onShare}
                                onDuplicate={onDuplicate}
                                onDelete={onDelete}
                                t={t}
                                isBrokerView={isBrokerView}
                                purchaseOffersModuleEnabled={purchaseOffersModuleEnabled}
                                shareRecipientCount={shareStats[property.id] ?? 0}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Contenu onglet Gestion des prêts (prêteur privé) */}
            {activeTab === 'loansManagement' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <PrivateLenderLoansPage
                  financedDeals={financedDealsAsLender}
                  properties={properties}
                  clients={clients}
                  onOpenProperty={onSelect}
                  initialDetailDeal={pendingLoanDetailDeal}
                  onConsumedInitialDetailDeal={onConsumePendingLoanDetail}
                />
              </div>
            )}

            {/* Contenu onglet Investisseurs de capitaux (prêteur privé) */}
            {activeTab === 'capitalInvestors' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <CapitalInvestorsPage />
              </div>
            )}

            {/* Contenu onglet Partagés avec moi (courtier) */}
            {activeTab === 'sharedWithMe' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-indigo-600" />
                  {t('home.sharedWithYou')}
                </h2>
                {sharedWithMe.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {sharedWithMe.map((item) => (
                      <SharedWithMeCard
                        key={item.id}
                        item={item}
                        onSelect={onSelectSharedWithMe}
                        onRemove={onRemoveSharedWithMe}
                        t={t}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-2">{t('home.noSharedWithMe')}</p>
                )}
              </div>
            )}

            {/* Contenu onglet Mon placement (investisseur de capitaux) */}
            {activeTab === 'investorFunds' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <InvestorFundsView fundInvestors={fundInvestorsAsInvestor} openFundInvestorId={openFundInvestorId} />
              </div>
            )}
          </div>
        ) : showClientTabs ? (
          <div className="mb-8">
            {privateLenderEmptyHub && (
              <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
                {t('home.privateLenderEmptyHubHelp')}
              </div>
            )}
            {/* Onglets Mes Deals | Projets financés | Partagés avec moi (client) */}
            <div className="bg-white rounded-t-lg shadow border-b border-gray-200 overflow-hidden">
              <div className="flex overflow-x-auto scrollbar-hide" data-onboarding="home-tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab('myAnalyses')}
                  className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                    activeTab === 'myAnalyses'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  {t('home.sectionMyAnalyses')}
                  {myAnalyses.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'myAnalyses' ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      {myAnalyses.length}
                    </span>
                  )}
                </button>
                {showFinancedProjectsTab && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('financedProjects')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'financedProjects'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <DollarSign className="w-4 h-4" />
                    {t('home.sectionFinancedProjects')}
                    {financedProjects.length > 0 && (
                      <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'financedProjects' ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                        {financedProjects.length}
                      </span>
                    )}
                  </button>
                )}
                {showLoansManagementTab && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('loansManagement')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'loansManagement'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <LayoutList className="w-4 h-4" />
                    {t('home.sectionLoansManagement')}
                  </button>
                )}
                {showCapitalInvestorsTab && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('capitalInvestors')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'capitalInvestors'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    {t('home.sectionCapitalInvestors')}
                  </button>
                )}
                {hasFundInvestorPlacements && (
                  <button
                    type="button"
                    onClick={() => setActiveTab('investorFunds')}
                    className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                      activeTab === 'investorFunds'
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <DollarSign className="w-4 h-4" />
                    {t('investorFunds.title')}
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'investorFunds' ? 'bg-teal-500' : 'bg-gray-300'}`}>
                      {fundInvestorsAsInvestor.length}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onDismissShareAddedBanner?.();
                    setActiveTab('sharedWithMe');
                  }}
                  className={`shrink-0 px-3 sm:px-4 py-3 font-medium text-sm rounded-t-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                    activeTab === 'sharedWithMe'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Share2 className="w-4 h-4" />
                  {t('home.tabSharedWithMe')}
                  {sharedWithMe.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${activeTab === 'sharedWithMe' ? 'bg-indigo-500' : 'bg-gray-300'}`}>
                      {sharedWithMe.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {activeTab === 'myAnalyses' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <div className="flex flex-col mb-4 md:flex-row md:justify-between md:items-center">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    {t('home.sectionMyAnalyses')}
                  </h2>
                  <button
                    type="button"
                    data-onboarding="home-new-deal"
                    onClick={onNew}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors mt-2 md:mt-0"
                  >
                    <Plus className="inline-block mr-1" size={18} />
                    {t('home.existing.new')}
                  </button>
                </div>
                {myAnalyses.length > 0 ? (
                  <>
                    {isBrokerView && purchaseOffersModuleEnabled ? (
                      <label className="flex items-start gap-2 text-sm text-gray-700 mb-3 cursor-pointer max-w-xl">
                        <input
                          type="checkbox"
                          className="mt-0.5 shrink-0"
                          checked={mandatesOnlyFilter}
                          onChange={(e) => setMandatesOnlyFilter(e.target.checked)}
                        />
                        <span>
                          <span className="font-medium block">{t('home.mandatesFilter')}</span>
                          <span className="text-xs text-gray-500 block mt-0.5">{t('home.mandatesFilterHint')}</span>
                        </span>
                      </label>
                    ) : null}
                    {myAnalysesDisplayed.length > 0 ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        {myAnalysesDisplayed.map((property, index) => (
                          <PropertyCard
                            key={property.id || index}
                            property={property}
                            index={index}
                            isOwner={isOwner}
                            isNewClientProperty={isNewClientProperty}
                            onSelect={onSelect}
                            onShare={onShare}
                            onDuplicate={onDuplicate}
                            onDelete={onDelete}
                            t={t}
                            isBrokerView={isBrokerView}
                            purchaseOffersModuleEnabled={purchaseOffersModuleEnabled}
                            shareRecipientCount={shareStats[property.id] ?? 0}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm py-2">{t('home.mandatesFilterEmpty')}</p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-sm py-2">{t('home.noMyAnalyses')}</p>
                )}
              </div>
            )}

            {activeTab === 'financedProjects' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  {t('home.sectionFinancedProjects')}
                </h2>
                {financedProjects.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {financedProjects.map((property, index) => (
                      <PropertyCard
                        key={property.id || index}
                        property={property}
                        index={index}
                        isOwner={isOwner}
                        isNewClientProperty={isNewClientProperty}
                        onSelect={onSelect}
                        onShare={onShare}
                        onDuplicate={onDuplicate}
                        onDelete={onDelete}
                        t={t}
                        isBrokerView={isBrokerView}
                        purchaseOffersModuleEnabled={purchaseOffersModuleEnabled}
                        shareRecipientCount={shareStats[property.id] ?? 0}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-2">{t('home.noMyAnalyses')}</p>
                )}
              </div>
            )}

            {activeTab === 'loansManagement' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <PrivateLenderLoansPage
                  financedDeals={financedDealsAsLender}
                  properties={properties}
                  clients={clients}
                  onOpenProperty={onSelect}
                  initialDetailDeal={pendingLoanDetailDeal}
                  onConsumedInitialDetailDeal={onConsumePendingLoanDetail}
                />
              </div>
            )}

            {activeTab === 'capitalInvestors' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <CapitalInvestorsPage />
              </div>
            )}

            {activeTab === 'sharedWithMe' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-indigo-600" />
                  {t('home.sharedWithYou')}
                </h2>
                {sharedWithMe.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2">
                    {sharedWithMe.map((item) => (
                      <SharedWithMeCard
                        key={item.id}
                        item={item}
                        onSelect={onSelectSharedWithMe}
                        onRemove={onRemoveSharedWithMe}
                        t={t}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-2">{t('home.noSharedWithMe')}</p>
                )}
              </div>
            )}

            {/* Mon placement (client view) */}
            {activeTab === 'investorFunds' && (
              <div className="bg-white rounded-b-lg shadow-lg p-4 sm:p-6 border border-t-0 border-gray-200">
                <InvestorFundsView fundInvestors={fundInvestorsAsInvestor} openFundInvestorId={openFundInvestorId} />
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex flex-col items-center mb-6 md:flex-row md:justify-between md:items-center">
              <h2 className="text-2xl font-semibold mb-4 md:mb-0">{t('home.existing.title')}</h2>
              <button
                type="button"
                data-onboarding="home-new-deal"
                onClick={onNew}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="inline-block mr-1" />
                {t('home.existing.new')}
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {properties.map((property, index) => (
                <PropertyCard
                  key={property.id || index}
                  property={property}
                  index={index}
                  isOwner={isOwner}
                  isNewClientProperty={isNewClientProperty}
                  onSelect={onSelect}
                  onShare={onShare}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  t={t}
                  isBrokerView={isBrokerView}
                  purchaseOffersModuleEnabled={purchaseOffersModuleEnabled}
                  shareRecipientCount={shareStats[property.id] ?? 0}
                />
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-sm text-gray-500 mt-8 mb-4 px-2">
          <button onClick={onAbout} className="hover:underline">
            {t('home.about')}
          </button>
          <CmbRates />
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;
